import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

type Course = {
  id: string;
  code: string;
  title: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type CourseContextType = {
  currentCourse: Course | null;
  isLoading: boolean;
  error: Error | null;
  refreshCourse: () => Promise<void>;
};

export const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider = ({ children }: { children: ReactNode }) => {
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth(); // Assuming you have an auth context

  const fetchUserCourse = useCallback(async () => {
    if (!user) {
      setCurrentCourse(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.debug('[CourseContext] Fetching course for user:', user.id);
      // Step 1: Get the user's enrollment (just the course_id)
      const { data: enrollment, error: enrollError } = await supabase
        .from('course_enrollments')
        .select('course_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (enrollError) {
        // Gracefully handle "no rows" case
        const code = (enrollError as { code?: string }).code;
        if (code === 'PGRST116') {
          console.warn('[CourseContext] No enrollment found for user.');
          setCurrentCourse(null);
          return;
        }
        console.error('Error fetching enrollment:', enrollError);
        throw enrollError;
      }

      if (!enrollment?.course_id) {
        console.warn('User has no course assignment');
        setCurrentCourse(null);
        return;
      }

      // Step 2: Fetch the course by id
      const { data: course, error: courseError } = await supabase
        .from('courses')
        .select('id, code, title, description, is_active, created_at, updated_at')
        .eq('id', enrollment.course_id)
        .single();

      if (courseError) {
        console.error('Error fetching course by id:', courseError);
        throw courseError;
      }

      setCurrentCourse(course as Course);
    } catch (err) {
      console.error('Error fetching user course:', err);
      setError(err instanceof Error ? err : new Error('Failed to load course'));
      setCurrentCourse(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch course when user changes
  useEffect(() => {
    fetchUserCourse();
  }, [fetchUserCourse]);

  return (
    <CourseContext.Provider
      value={{
        currentCourse,
        isLoading,
        error,
        refreshCourse: fetchUserCourse,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourse = (): CourseContextType => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};

// Helper hook for components that require a course
export const useRequiredCourse = (): Course => {
  const { currentCourse, isLoading } = useCourse();
  
  if (isLoading) {
    throw new Promise(resolve => setTimeout(resolve, 0)); // Let Suspense handle loading
  }
  
  if (!currentCourse) {
    throw new Error('No course assigned to user');
  }
  
  return currentCourse;
};
